// The admin dashboard's data, and the one write the app makes to feed it.
//
// Nothing here is a third-party analytics SDK on purpose: every number the
// dashboard shows is derived from Firebase Auth and the users collection,
// which the app already owns and which stays correct whether or not a visitor
// blocks trackers.
import { useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import type { User } from 'firebase/auth';
import { functions } from '../firebase';
import { clearPreviewPlaysAtSignup, previewPlaysAtSignup } from '../previewGate';

/** What a paying member is worth a month, in cents — the monthly plan price.
 *  Used to turn a headcount into revenue and a spend into a cost per member. */
export const MEMBERSHIP_MONTHLY_CENTS = 400;

/** The line between a promoter worth paying again and one to stop. Set from
 *  the plan's own economics: $4/mo nets ~$3.58 after Stripe, so a member who
 *  stays ~10 months is worth ~$36, and paying back inside three months puts
 *  the ceiling near $12. Past $25 a member costs more than they ever return. */
export const CPA_REPEAT_CENTS = 1200;
export const CPA_STOP_CENTS = 2500;

/** What one paying member returns over their life, in cents. $4/mo nets
 *  ~$3.58 after Stripe and members stay ~10 months, so ~$36. This is the
 *  number every target on the plan panel is derived from — change it here and
 *  the break-even and the win line move with it. */
export const MEMBER_LTV_CENTS = 3600;

export type Verdict = 'repeat' | 'watch' | 'stop' | 'pending';

/** What a promoter's spend bought, judged against the thresholds above.
 *  Nothing is judged until someone has actually paid: with a 30-day trial a
 *  fresh campaign has no cost per member yet, and calling it dead at day 10 is
 *  how you kill something that was working. */
export function verdictFor(row: { spentCents: number; paying: number; pending: number }): Verdict {
  if (!row.spentCents) return 'pending';
  if (row.paying === 0) return row.pending > 0 ? 'pending' : 'stop';
  const cpa = row.spentCents / row.paying;
  if (cpa <= CPA_REPEAT_CENTS) return 'repeat';
  if (cpa <= CPA_STOP_CENTS) return 'watch';
  return 'stop';
}

export interface PromoterRow {
  key: string;
  label: string;
  signups: number;
  trials: number;
  paying: number;
  comped: number;
  /** Still inside a trial, so their verdict isn't knowable yet. */
  pending: number;
  spentCents: number;
}

export interface UserRow {
  uid: string;
  name: string;
  email: string;
  created: number | null;
  lastSeen: number | null;
  plan: 'pro' | 'trial' | 'code' | 'expired' | null;
  cancelling: boolean;
  source: string | null;
}

export interface Cohort { eligible: number; back: number }

export interface Metrics {
  generatedAt: number;
  hero: { paying: number; trialing: number; comped: number; expired: number; accounts: number; newThisMonth: number };
  funnel: {
    signups7: number; signupsPrev7: number; signups30: number; signupsPrev30: number;
    trialing: number; paying: number; neverStarted: number;
  };
  promoters: PromoterRow[];
  money: { payingCents: number; promoMrrCents: number; cancelling: number; comped: number; expired: number };
  retention: { active7: number; active30: number; week1: Cohort; week2: Cohort; week4: Cohort };
  sponsors: {
    live: number; pending: number; railCount: number;
    cards: { uid: string; handle: string; name: string; status: string; ltvCents: number }[];
  };
  content: { topStarred: { id: string; stars: number }[] };
  users: UserRow[];
}

export function fetchMetrics(): Promise<Metrics> {
  return httpsCallable<void, Metrics>(functions, 'metrics')().then((r) => r.data);
}

export function setPromoterSpend(key: string, label: string, spentCents: number): Promise<unknown> {
  return httpsCallable(functions, 'setPromoterSpend')({ key, label, spentCents });
}

/** Tell the server this person was here, at most once a day. The source tags
 *  ride along on every call and the server keeps only the first — the app
 *  cannot know whether this account is new, and the server can. */
export function useRecordVisit(user: User | null | undefined) {
  useEffect(() => {
    if (!user) return;
    const today = new Date().toISOString().slice(0, 10);
    const key = `gnosis-visit-${user.uid}`;
    if (localStorage.getItem(key) === today) return;
    localStorage.setItem(key, today);
    httpsCallable(functions, 'recordVisit')({
      ref: localStorage.getItem('gnosis-ref') || undefined,
      reward: localStorage.getItem('gnosis-ref-reward') || undefined,
      utmSource: localStorage.getItem('gnosis-utm-source') || undefined,
      utmCampaign: localStorage.getItem('gnosis-utm-campaign') || undefined,
      // How many free clips it took to earn the account, when they came in
      // through the preview. Kept first-write-wins on the server, like source.
      previewPlays: previewPlaysAtSignup(),
    }).then(() => {
      clearPreviewPlaysAtSignup();
    }).catch(() => {
      // A missed ping is a missed day of "last seen", never a broken app.
      localStorage.removeItem(key);
    });
  }, [user]);
}
